import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, ArrowLeft, Mail, Phone, User, Calendar, Award, ChefHat, Coffee, Droplets, CupSoda, UtensilsCrossed, Calculator, Sparkles, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO, { SEO_CONFIG } from '@/components/SEO';

const CareersPage = () => {
  const navigate = useNavigate();

  const positions = [
    { name: 'Chef', icon: ChefHat },
    { name: 'Sous Chef', icon: UtensilsCrossed },
    { name: 'Dishwasher', icon: Droplets },
    { name: 'Smoothie & Juice Drink Maker', icon: CupSoda },
    { name: 'Barista', icon: Coffee },
    { name: 'Waiter - Waitress - Server - Attendant', icon: UtensilsCrossed },
    { name: 'Cashier', icon: Calculator },
    { name: 'Cleaner', icon: Sparkles },
    { name: 'Security', icon: Shield }
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="careers-page">
      <SEO {...SEO_CONFIG.careers} path="/careers" />
      
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-muted-foreground hover:text-foreground"
          data-testid="careers-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Briefcase className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-foreground mb-4">
            Careers
          </h1>
          <p className="text-muted-foreground text-lg">
            Join our team at Bulaccino Cafe
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-3xl border border-border p-8 sm:p-10 shadow-sm"
        >
          {/* Hiring Announcement */}
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl text-foreground mb-2">
              Bulaccino Cafe is hiring.
            </h2>
            <p className="text-muted-foreground">
              Looking to fill the following positions:
            </p>
          </div>

          {/* Positions List */}
          <div className="mb-10">
            <div className="grid sm:grid-cols-2 gap-3">
              {positions.map((position, index) => {
                const IconComponent = position.icon;
                return (
                  <motion.div
                    key={position.name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium text-foreground break-words">{position.name}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Application Instructions */}
          <div className="bg-primary/5 rounded-2xl p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-primary" />
              <h3 className="font-heading text-xl text-foreground">How to Apply</h3>
            </div>
            <p className="text-foreground mb-4">
              Send an email to{' '}
              <a 
                href="mailto:career@bulaccinocafe.com" 
                className="text-primary font-semibold hover:underline"
                data-testid="careers-email-link"
              >
                career@bulaccinocafe.com
              </a>
            </p>
            
            <div className="space-y-4 text-muted-foreground">
              <p className="font-medium text-foreground">Please include:</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Your work experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>How many days per week you are available to work (6 is the max)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Why we should select you for an interview and the job over anyone else</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-muted/30 rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-6 h-6 text-primary" />
              <h3 className="font-heading text-xl text-foreground">Your Information</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              Provide your name, phone number and email address and we will reach out to you if you are selected for an interview.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" /> Name
              </span>
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> Phone Number
              </span>
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email Address
              </span>
            </div>
          </div>
        </motion.div>

        {/* Footer Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-muted-foreground text-sm mt-8"
        >
          We look forward to hearing from you!
        </motion.p>
      </div>
    </div>
  );
};

export default CareersPage;
